import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, RevealLines, easeSmooth } from "@/lib/motion";
import { ArrowUpRight } from "lucide-react";
import ShareButtons from "@/components/site/ShareButtons";

const projects = [
  {
    id: "marble-galley",
    name: "Marble Galley Kitchen",
    tag: "Residential",
    year: "2025",
    location: "London ON",
    img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/lvvtqn4x_PHOTO-2026-05-23-10-27-00.jpg",
    desc: "A galley kitchen re-imagined in Calacatta marble — polished nickel fittings, custom Roman shades and a tall, considered pantry line.",
  },
  {
    id: "quartz-island",
    name: "Quartz Island Renovation",
    tag: "Residential",
    year: "2025",
    location: "London ON",
    img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/19qd0i05_PHOTO-2026-05-23-10-27-00%20%281%29.jpg",
    desc: "Full kitchen rebuild — a monolithic quartz island, matte black hardware, and a floating orbital pendant as the room’s single ornament.",
  },
  {
    id: "diagonal-basement",
    name: "Diagonal Basement Suite",
    tag: "Residential",
    year: "2024",
    location: "London ON",
    img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/9ajn1v3v_PHOTO-2026-05-23-10-27-00%20%282%29.jpg",
    desc: "A finished lower level built around a diagonal panelled feature wall — new kitchen, hardwood plank floor, and a room that no longer feels below grade.",
  },
  {
    id: "atrium-kitchen",
    name: "Atrium Kitchen & Bar",
    tag: "Residential",
    year: "2024",
    location: "Komoka ON",
    img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/7hexdmyw_PHOTO-2026-05-23-10-27-00%20%283%29.jpg",
    desc: "A vaulted-timber ceiling drops into a black-and-brass island. Backlit uppers, integrated wine display, and a marble waterfall that anchors the room.",
  },
  {
    id: "cottage-classic",
    name: "Cottage Classic Kitchen",
    tag: "Residential",
    year: "2024",
    location: "Byron, London ON",
    img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/kwixu5t0_PHOTO-2026-05-23-10-27-00%20%284%29.jpg",
    desc: "A transitional kitchen with a soft blue island, veined granite and warm oak flooring — a family room that happens to cook.",
  },
  {
    id: "ridgeline",
    name: "Ridgeline House",
    tag: "Residential",
    year: "2024",
    location: "Byron, London ON",
    img: "https://images.unsplash.com/photo-1679364297777-1db77b6199be?auto=format&fit=crop&w=1600&q=80",
    desc: "A 6,400 sq ft private estate carved into the ridge — solid oak floors, hand-troweled plaster, cantilevered terrace.",
  },
  {
    id: "halcyon",
    name: "Halcyon Loft",
    tag: "Residential",
    year: "2024",
    location: "Downtown London ON",
    img: "https://images.unsplash.com/photo-1564078516393-cf04bd966897?auto=format&fit=crop&w=1600&q=80",
    desc: "An adaptive-reuse penthouse. We removed thirty years of drop-ceiling and let the original beams do the talking.",
  },
  {
    id: "orchard",
    name: "Orchard Residence",
    tag: "Residential",
    year: "2023",
    location: "Talbotville ON",
    img: "https://images.pexels.com/photos/13203184/pexels-photo-13203184.jpeg",
    desc: "A quiet country home wrapped in a stone plinth — the pool sits in shadow, the house sits in silence.",
  },
  {
    id: "linden",
    name: "Linden Row House",
    tag: "Residential",
    year: "2023",
    location: "Old North, London ON",
    img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80",
    desc: "A heritage terrace reimagined — restored brick, walnut millwork and a skylit stair core that flooded the plan.",
  },
  {
    id: "birchmere",
    name: "Birchmere Estate",
    tag: "Residential",
    year: "2023",
    location: "Komoka ON",
    img: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1600&q=80",
    desc: "A single-storey estate on 12 acres — cedar cladding, radiant limestone floors, dark bronze glazing.",
  },
  {
    id: "quarry-house",
    name: "Quarry House",
    tag: "Residential",
    year: "2022",
    location: "St. Thomas ON",
    img: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1600&q=80",
    desc: "Built into the edge of a disused quarry. Board-formed concrete walls hold a warm oak interior.",
  },
  {
    id: "hollow-oak",
    name: "Hollow Oak Retreat",
    tag: "Residential",
    year: "2022",
    location: "Port Stanley ON",
    img: "https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=1600&q=80",
    desc: "A lakeside retreat with a shou-sugi-ban façade, sunken living room and a single, quiet fireplace wall.",
  },
  {
    id: "willow-lane",
    name: "Willow Lane Renovation",
    tag: "Residential",
    year: "2022",
    location: "Wortley Village, London ON",
    img: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1600&q=80",
    desc: "Gut renovation of a 1920s cottage — original crown restored, kitchen extended into a plaster-vaulted volume.",
  },
  {
    id: "cornice",
    name: "Cornice Courtyard",
    tag: "Commercial",
    year: "2023",
    location: "Old East Village",
    img: "https://images.pexels.com/photos/31735042/pexels-photo-31735042.jpeg",
    desc: "Boutique retail courtyard behind a preserved 1908 façade. New steel and old brick, in dialogue.",
  },
  {
    id: "atelier",
    name: "Atelier Interior",
    tag: "Commercial",
    year: "2022",
    location: "Richmond Row",
    img: "https://images.unsplash.com/photo-1647735282077-c12699af40be?auto=format&fit=crop&w=1600&q=80",
    desc: "A studio-slash-showroom for a fashion label. Blackened steel armatures, warm oak, single skylight.",
  },
];

const filters = ["All", "Residential", "Commercial"];

export default function Projects() {
  const [active, setActive] = useState("All");
  const [open, setOpen] = useState(null);

  const visible = useMemo(
    () => (active === "All" ? projects : projects.filter((p) => p.tag === active)),
    [active]
  );

  return (
    <main data-testid="projects-page" className="pt-28 md:pt-32">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-12">
        <span className="eyebrow">Portfolio</span>
        <div className="mt-6">
          <RevealLines
            className="font-display text-[12vw] md:text-[9vw] leading-[0.9] tracking-tighter"
            lines={[
              <>Selected</>,
              <><em className="hero-italic">landmarks.</em></>,
            ]}
            stagger={0.14}
          />
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-8">
        <div className="flex items-center gap-2 md:gap-3 flex-wrap border-t border-[#D9D4C7] pt-6" data-testid="project-filters">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setActive(f)}
              data-testid={`filter-${f.toLowerCase()}`}
              className={`px-4 py-2 font-grotesk text-[11px] uppercase tracking-[0.22em] border transition-colors duration-300 ${
                active === f
                  ? "bg-[#1A1A1A] text-[#FAF9F6] border-[#1A1A1A]"
                  : "bg-transparent text-[#1A1A1A] border-[#D9D4C7] hover:border-[#1A1A1A]"
              }`}
            >
              {f}
            </button>
          ))}
          <div className="ml-auto font-grotesk text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A]">
            {visible.length} {visible.length === 1 ? "project" : "projects"}
          </div>
        </div>
      </section>

      {/* Editorial grid */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.8, ease: easeSmooth, delay: (i % 6) * 0.05 }}
                className={`group cursor-pointer ${
                  i % 6 === 0
                    ? "md:col-span-8"
                    : i % 6 === 1
                    ? "md:col-span-4"
                    : i % 6 === 2
                    ? "md:col-span-5"
                    : i % 6 === 3
                    ? "md:col-span-7"
                    : "md:col-span-6"
                }`}
                onClick={() => setOpen(p)}
                data-testid={`project-card-${p.id}`}
              >
                <div className="clip-frame aspect-[4/3] border border-[#D9D4C7]">
                  <img src={p.img} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="mt-5 flex items-baseline justify-between">
                  <div>
                    <div className="font-grotesk text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A]">
                      {p.tag} · {p.year}
                    </div>
                    <div className="mt-2 font-display text-2xl md:text-3xl leading-tight">
                      {p.name}
                    </div>
                    <div className="mt-1 font-grotesk text-[12px] text-[#4A4A4A]">{p.location}</div>
                  </div>
                  <ArrowUpRight className="w-5 h-5 opacity-40 group-hover:opacity-100 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500" />
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>
      </section>

      {/* Detail lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[70] bg-[#1A1A1A]/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={() => setOpen(null)}
            data-testid="project-detail"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.6, ease: easeSmooth }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-6xl bg-[#FAF9F6] max-h-[92vh] overflow-y-auto no-scrollbar"
            >
              <div className="clip-frame aspect-[16/9]">
                <img src={open.img} alt={open.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-6 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-8">
                <div className="md:col-span-8">
                  <div className="font-grotesk text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A]">
                    {open.tag} · {open.year} · {open.location}
                  </div>
                  <h3 className="mt-3 font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">
                    {open.name}
                  </h3>
                  <p className="mt-6 font-grotesk text-[15px] text-[#4A4A4A] leading-[1.7] max-w-2xl">
                    {open.desc}
                  </p>
                  <ShareButtons project={open} />
                </div>
                <div className="md:col-span-4 md:pl-6 border-t md:border-t-0 md:border-l border-[#D9D4C7] pt-6 md:pt-0">
                  <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#4A4A4A] mb-4">
                    At a glance
                  </div>
                  <ul className="space-y-2 font-grotesk text-sm">
                    <li className="flex justify-between border-b border-[#D9D4C7] pb-2">
                      <span className="text-[#4A4A4A]">Sector</span><span>{open.tag}</span>
                    </li>
                    <li className="flex justify-between border-b border-[#D9D4C7] pb-2">
                      <span className="text-[#4A4A4A]">Completed</span><span>{open.year}</span>
                    </li>
                    <li className="flex justify-between border-b border-[#D9D4C7] pb-2">
                      <span className="text-[#4A4A4A]">Location</span><span>{open.location}</span>
                    </li>
                  </ul>
                  <Link to="/contact" className="btn-sharp mt-6 w-full justify-center" data-testid="detail-cta">
                    <span>Enquire</span>
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="py-28 px-6 md:px-10 max-w-[1600px] mx-auto text-center">
        <Reveal>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Yours <em className="hero-italic">next.</em>
          </h2>
          <div className="mt-10">
            <Link to="/contact" className="btn-sharp" data-testid="projects-cta">
              <span>Start an Enquiry</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
